import {
  prisma,
  ResourceType,
  ActionType,
  QueueRedisType,
  QueueConfiguration,
} from "client";
// QUEUE CONFIGURATION SERVICES
export const createQueueConfiguration = async (
  userId: string,
  whiteLabelId: string,
  data: {
    isActive: boolean;
    redisType: QueueRedisType;
    redisUrl?: string;
    qstashUrl?: string;
    qstashToken?: string;
    redisToken?: string;
    orgCanConfigure?: boolean;
  }
) => {
  try {
    const existing = await prisma.queueConfiguration.findFirst({
      where: { whiteLabelId },
    });

    let queueConfig: QueueConfiguration;

    if (existing) {
      queueConfig = await prisma.queueConfiguration.update({
        where: { id: existing.id },
        data: {
          isActive: data.isActive,
          redisType: data.redisType,
          redisUrl: data.redisUrl,
          qstashUrl: data.qstashUrl,
          qstashToken: data.qstashToken,
          redisToken: data.redisToken,
          ...(data.orgCanConfigure !== undefined && {
            orgCanConfigure: data.orgCanConfigure,
          }),
        },
      });
    } else {
      queueConfig = await prisma.queueConfiguration.create({
        data: {
          whiteLabelId,
          isActive: data.isActive,
          redisType: data.redisType,
          redisUrl: data.redisUrl,
          qstashUrl: data.qstashUrl,
          qstashToken: data.qstashToken,
          redisToken: data.redisToken,
          orgCanConfigure: data.orgCanConfigure ?? false,
        },
      });
    }

    return { queueConfig };
  } catch (error) {
    return { error };
  }
};

export const getQueueConfiguration = async (
  userId: string,
  whiteLabelId: string
) => {
  try {
    const queueConfig = await prisma.queueConfiguration.findFirst({
      where: { whiteLabelId },
    });

    return { queueConfig };
  } catch (error) {
    return { error };
  }
};

export const updateQueueConfiguration = async (
  userId: string,
  whiteLabelId: string,
  data: {
    isActive?: boolean;
    redisType?: QueueRedisType;
    redisUrl?: string;
    qstashUrl?: string;
    qstashToken?: string;
    redisToken?: string;
    orgCanConfigure?: boolean;
  }
) => {
  try {
    const existing = await prisma.queueConfiguration.findFirst({
      where: { whiteLabelId },
    });

    if (!existing) {
      return { error: "Queue configuration not found" };
    }

    const queueConfig = await prisma.queueConfiguration.update({
      where: { id: existing.id },
      data: {
        ...data,
      },
    });

    return { queueConfig };
  } catch (error) {
    return { error };
  }
};

export async function upsertQueueConfiguration(
  whiteLabelId: string,
  config: Partial<QueueConfiguration>
) {
  try {
    const existing = await prisma.queueConfiguration.findFirst({
      where: { whiteLabelId },
    });

    let updatedConfig: QueueConfiguration;

    if (existing) {
      updatedConfig = await prisma.queueConfiguration.update({
        where: { id: existing.id },
        data: {
          isActive: config.isActive ?? existing.isActive,
          redisType: config.redisType ?? existing.redisType,
          redisUrl: config.redisUrl ?? existing.redisUrl,
          qstashUrl: config.qstashUrl ?? existing.qstashUrl,
          ...(config.qstashToken !== undefined && {
            qstashToken: config.qstashToken,
          }),
          ...(config.redisToken !== undefined && {
            redisToken: config.redisToken,
          }),
          ...(config.orgCanConfigure !== undefined && {
            orgCanConfigure: config.orgCanConfigure,
          }),
        },
      });
    } else {
      updatedConfig = await prisma.queueConfiguration.create({
        data: {
          whiteLabelId,
          isActive: config.isActive ?? false,
          redisType: config.redisType ?? "UPSTASH",
          redisUrl: config.redisUrl ?? "",
          qstashUrl: config.qstashUrl ?? "",
          qstashToken: config.qstashToken ?? "",
          redisToken: config.redisToken ?? "",
          orgCanConfigure: config.orgCanConfigure ?? false,
        },
      });
    }
    console.log(updatedConfig);

    return { queueConfig: updatedConfig, error: null };
  } catch (error) {
    console.error("Error updating queue configuration:", error);
    return {
      queueConfig: null,
      error: "Failed to update queue configuration",
    };
  }
}

export const toggleQueueStatus = async (
  userId: string,
  whiteLabelId: string,
  isActive: boolean
) => {
  try {
    const existing = await prisma.queueConfiguration.findFirst({
      where: { whiteLabelId },
    });
    if (!existing) {
      return { error: "Queue configuration not found" };
    }
    const queueConfig = await prisma.queueConfiguration.update({
      where: { id: existing.id },
      data: { isActive },
    });

    return { queueConfig };
  } catch (error) {
    return { error };
  }
};

export const getQueueConfigurationPublic = async (whiteLabelId: string) => {
  try {
    const queueConfig = await prisma.queueConfiguration.findFirst({
      where: { whiteLabelId },
    });
    return { queueConfig };
  } catch (error) {
    return { error };
  }
};

export const getQueueConfigurationByOrganizationId = async (
  organizationId: string
) => {
  try {
    const queueConfig = await prisma.queueConfiguration.findFirst({
      where: { organizationId },
    });

    return { queueConfig };
  } catch (error) {
    return { error };
  }
};

export const getQueueConfigurationPublicForOrganization = async (
  organizationId: string
) => {
  try {
    const queueConfig = await prisma.queueConfiguration.findFirst({
      where: { organizationId },
    });

    return { queueConfig };
  } catch (error) {
    return { error };
  }
};

export const upsertQueueConfigurationForOrganization = async (
  organizationId: string,
  config: Partial<QueueConfiguration>
) => {
  try {
    const existing = await prisma.queueConfiguration.findFirst({
      where: { organizationId },
    });

    if (existing) {
      const updatedConfig = await prisma.queueConfiguration.update({
        where: { id: existing.id },
        data: {
          isActive: config.isActive ?? existing.isActive,
          redisType: config.redisType ?? existing.redisType,
          redisUrl: config.redisUrl ?? existing.redisUrl,
          qstashUrl: config.qstashUrl ?? existing.qstashUrl,
          ...(config.qstashToken !== undefined && {
            qstashToken: config.qstashToken,
          }),
          ...(config.redisToken !== undefined && {
            redisToken: config.redisToken,
          }),
        },
      });
      return { queueConfig: updatedConfig, error: null };
    }

    const created = await prisma.queueConfiguration.create({
      data: {
        organizationId,
        isActive: config.isActive ?? false,
        redisType: config.redisType ?? "UPSTASH",
        redisUrl: config.redisUrl ?? "",
        qstashUrl: config.qstashUrl ?? "",
        qstashToken: config.qstashToken ?? "",
        redisToken: config.redisToken ?? "",
      },
    });
    return { queueConfig: created, error: null };
  } catch (error) {
    console.error("Error updating queue configuration for organization:", error);
    return {
      queueConfig: null,
      error: "Failed to update queue configuration",
    };
  }
};

export const deleteQueueConfigurationByOrganizationId = async (
  organizationId: string
) => {
  try {
    const result = await prisma.queueConfiguration.deleteMany({
      where: { organizationId },
    });
    return { count: result.count };
  } catch (error) {
    console.error("Error deleting queue configuration:", error);
    return { error };
  }
};
